import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { 
  AlertTriangle, Check, BookOpen, Sparkles, 
  ArrowRight, Edit3, Save, RefreshCw 
} from 'lucide-react';
import StatusBadge from '../components/StatusBadge';
import { useApp } from '../context/AppContext';
import { ReviewStatus, Clause, ReviewResult, ProjectStatus } from '../types';
import { POLLING_INTERVALS, DISCLAIMER } from '../constants';
import { cleanDuplicatedNumbering, getClauseNumberLabel, getClauseTitleLabel } from '../utils/clauses';

const isStructuralHeadingClause = (clause: Clause, allClauses: Clause[]): boolean => {
  const number = (clause.clause_no || '').trim();
  const title = (clause.title || '').trim();
  const content = cleanDuplicatedNumbering(clause.content || '').trim();
  if (!number || !title) return false;

  const isNumberedClause = /^\d+(?:\.\d+)*$/.test(number) || /^第?[一二三四五六七八九十百千零0-9]+[条章节]?$/.test(number);
  if (!isNumberedClause) return false;

  const hasChildren = /^\d+(?:\.\d+)*$/.test(number) && allClauses.some(item => {
    const other = (item.clause_no || '').trim();
    return other !== number && other.startsWith(`${number}.`);
  });

  const stripNumberPrefix = (value: string) => value.replace(new RegExp(`^${number.replace(/\./g, '\\.')}\\s*`), '');
  const compactTitle = title.replace(/\s+/g, '');
  const compactContent = stripNumberPrefix(content).replace(/\s+/g, '');
  const contentWithoutTitle = compactContent.replace(compactTitle, '');
  const isShortTitle = compactContent.length <= 96 || contentWithoutTitle.length <= 18;
  const hasSubstantiveLanguage = /应当|应该|必须|不得|禁止|有权|负责|承担|保证|承诺|同意|确保|开具|补偿|违约金|逾期|扣款|签字盖章|生效日期|书面通知|人民币|金额|价款.{0,12}(?:为|人民币|元)|期限.{0,12}(?:为|自|至|届满)|(?:支付|结算).{0,16}(?:价款|费用|款项|人民币|元|发票)|赔偿.{0,16}(?:责任|损失|费用)|shall|must|may not|will\s+(?:pay|provide|deliver|notify|terminate)|agrees?\s+to|is\s+liable|pay(?:ment)?\s+shall|breach/i.test(content);
  const looksLikeTitleOnly = compactContent === compactTitle || contentWithoutTitle.length <= 6 || compactContent.length <= 96;
  if (!hasChildren && !looksLikeTitleOnly && allClauses.length > 1) return false;

  return isShortTitle && !hasSubstantiveLanguage;
};

const compareClauses = (a: Clause, b: Clause): number => {
  return (a.sequence || 0) - (b.sequence || 0);
};

const ReviewPage: React.FC = () => {
  const navigate = useNavigate();
  const { id: projectId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { 
    currentProject, 
    clauses, 
    reviewResults,
    loadProject,
    loadClauses,
    startReview,
    pollReviewStatus,
    loadReviewResults,
    reviewSingleClause,
    saveUserEdit,
  } = useApp();
  
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [userEditText, setUserEditText] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const targetClauseId = searchParams.get('clauseId');
  const reviewableClauses = clauses
    .filter(clause => !isStructuralHeadingClause(clause, clauses))
    .sort(compareClauses);

  // Load project and clauses
  useEffect(() => {
    if (projectId && projectId !== 'new') {
      loadProject(projectId).catch(() => {
        // 如果加载失败，重定向到上传页面
        navigate(`/project/${projectId}/upload`);
      });
      loadClauses(projectId).catch(() => {
        // 忽略条款加载错误
      });
    } else if (projectId === 'new') {
      // 新项目需要先上传文件
      navigate('/project/new/upload');
    }
  }, [projectId]);

  // Handle project status
  useEffect(() => {
    if (!currentProject) return;
    
    if (currentProject.status === ProjectStatus.REVIEWING) {
      setProcessing(true);
      startPolling();
    } else if (currentProject.status === ProjectStatus.REVIEWED ||
               currentProject.status === ProjectStatus.COMPLETED) {
      setProcessing(false);
      setProgress(100);
      loadReviewResults(currentProject.id);
    } else if (currentProject.status === ProjectStatus.PARSED) {
      // Ready to start review
      setProcessing(false);
    }
    
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [currentProject?.status]);

  // Auto-select first clause with result
  useEffect(() => {
    if (!selectedId && reviewableClauses.length > 0) {
      const firstWithResult = reviewableClauses.find(c => reviewResults.has(c.id));
      if (firstWithResult) {
        setSelectedId(firstWithResult.id);
      } else if (reviewableClauses.length > 0) {
        setSelectedId(reviewableClauses[0].id);
      }
    }
  }, [reviewableClauses, reviewResults, selectedId]);

  useEffect(() => {
    if (!targetClauseId || reviewableClauses.length === 0) return;
    const target = reviewableClauses.find(c => c.id === targetClauseId);
    if (!target) return;

    setSelectedId(target.id);
    window.setTimeout(() => {
      document
        .getElementById(`review-clause-${target.id}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 0);
  }, [targetClauseId, reviewableClauses]);

  const startPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }
    
    pollingRef.current = setInterval(async () => {
      if (!projectId) return;
      
      const { completed, progress: p } = await pollReviewStatus(projectId);
      setProgress(p);
      
      if (completed) {
        setProcessing(false);
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
        }
      }
    }, POLLING_INTERVALS.REVIEW_STATUS);
  };

  const handleStartReview = async () => {
    if (!projectId || projectId === 'new') return;
    
    setProcessing(true);
    setProgress(0);
    
    try {
      await startReview(projectId);
      startPolling();
    } catch (e) {
      setProcessing(false);
    }
  };

  const handleReReviewClause = async (clauseId: string) => {
    if (!projectId || projectId === 'new') return;
    
    try {
      await reviewSingleClause(projectId, clauseId, true);
    } catch (e) {
      console.error('Re-review failed:', e);
    }
  };

  const handleSaveUserEdit = async () => {
    if (!projectId || projectId === 'new' || !selectedId || !userEditText.trim()) return;
    
    setSavingEdit(true);
    try {
      await saveUserEdit(projectId, selectedId, [userEditText], userEditText);
      setUserEditText('');
    } catch (e) {
      console.error('Save edit failed:', e);
    } finally {
      setSavingEdit(false);
    }
  };

  // Get clause with review result
  const getClauseWithReview = (clause: Clause): Clause & { review?: ReviewResult } => {
    return {
      ...clause,
      review: reviewResults.get(clause.id),
    };
  };

  const selectedClause = selectedId ? reviewableClauses.find(c => c.id === selectedId) : undefined;
  const activeClause = selectedClause
    ? getClauseWithReview(selectedClause)
    : null;

  const completedCount = Array.from(reviewResults.values()).filter(
    r => r.status !== ReviewStatus.PENDING && reviewableClauses.some(clause => clause.id === r.clause_id)
  ).length;

  // Show start review if not yet started
  if (!processing && reviewResults.size === 0 && currentProject?.status === ProjectStatus.PARSED) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center bg-white">
        <div className="text-center max-w-md">
          <h2 className="text-xl font-bold text-slate-800 mb-2">准备开始 AI 审查</h2>
          <p className="text-slate-500 mb-6">
            共 {reviewableClauses.length} 个实质条款将被逐一审核。一级目录标题不会进入合规审查。
          </p>
          <button
            onClick={handleStartReview}
            className="flex items-center gap-2 px-8 py-3 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 shadow-lg mx-auto"
          >
            <Sparkles size={20} />
            开始 AI 审查
          </button>
          
          <div className="mt-8 p-4 bg-amber-50 border border-amber-200 rounded-lg text-left">
            <p className="text-xs text-amber-800">{DISCLAIMER.zh}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-slate-100">
      {/* Left: Clause List & Status */}
      <div className="w-1/3 min-w-[320px] max-w-md bg-white border-r border-slate-200 flex flex-col">
        <div className="p-4 border-b border-slate-200">
          <div className="flex justify-between items-center mb-2">
            <h2 className="font-bold text-slate-800">审查进度</h2>
            <span className="text-xs font-mono text-slate-500">{completedCount}/{reviewableClauses.length}</span>
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div 
              className="h-full bg-slate-900 transition-all duration-300" 
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {reviewableClauses.map((clause) => {
            const review = reviewResults.get(clause.id);
            const status = review?.status || ReviewStatus.PENDING;
            const clauseNumber = getClauseNumberLabel(clause);
            const cleanedTitle = getClauseTitleLabel(clause);
            const cleanedContent = cleanDuplicatedNumbering(clause.content);
            
            return (
              <div 
                key={clause.id}
                id={`review-clause-${clause.id}`}
                onClick={() => setSelectedId(clause.id)}
                className={`px-4 py-3 border-b border-slate-100 cursor-pointer transition-colors hover:bg-slate-50 ${
                  selectedId === clause.id ? 'bg-blue-50 border-l-4 border-l-blue-600' : 'border-l-4 border-l-transparent'
                }`}
              >
                <div className="flex justify-between items-center gap-3 mb-1">
                  <span className="font-mono text-xs font-bold text-slate-500 shrink-0">
                    {clauseNumber}
                  </span>
                  <StatusBadge status={status} size="sm" />
                </div>
                {cleanedTitle && cleanedTitle !== clauseNumber && (
                  <h4 className="font-semibold text-sm text-slate-800 line-clamp-1">
                    {cleanedTitle}
                  </h4>
                )}
                <p className="text-xs text-slate-500 line-clamp-2 mt-1 leading-relaxed">
                  {status === ReviewStatus.PENDING ? '等待分析...' : cleanedContent}
                </p>
              </div>
            );
          })}
          {reviewableClauses.length === 0 && (
            <div className="p-6 text-center text-sm text-slate-400">
              暂无可审查的实质条款
            </div>
          )}
        </div>
        
        <div className="p-4 border-t border-slate-200 bg-slate-50">
          <button 
            disabled={processing}
            onClick={() => navigate(`/project/${projectId}/report`)}
            className={`w-full flex items-center justify-center gap-2 py-3 rounded-lg font-bold transition-all ${
              processing 
                ? 'bg-slate-300 text-white cursor-wait' 
                : 'bg-slate-900 text-white hover:bg-slate-800 shadow-lg'
            }`}
          >
            {processing ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                AI 正在审查... {Math.round(progress)}%
              </>
            ) : (
              <>
                查看总结报告
                <ArrowRight size={16} />
              </>
            )}
          </button>
        </div>
      </div>

      {/* Right: Detail Panel */}
      <div className="flex-1 bg-slate-50 p-6 overflow-y-auto">
        {activeClause ? (
          <div className="max-w-3xl mx-auto space-y-6">
            {/* Header Card */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h1 className="text-xl font-bold text-slate-900 mb-1">
                    {getClauseNumberLabel(activeClause)} {getClauseTitleLabel(activeClause)}
                  </h1>
                  <div className="flex gap-2 items-center">
                    <StatusBadge status={activeClause.review?.status || ReviewStatus.PENDING} />
                    {activeClause.review?.confidence && (
                      <span className="text-xs bg-slate-100 px-2 py-1 rounded text-slate-600">
                        置信度: {(activeClause.review.confidence * 100).toFixed(0)}%
                      </span>
                    )}
                    <button
                      onClick={() => handleReReviewClause(activeClause.id)}
                      className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"
                    >
                      <RefreshCw size={12} />
                      重新审核
                    </button>
                  </div>
                </div>
              </div>
              <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg text-slate-800 font-serif leading-relaxed text-lg">
                {cleanDuplicatedNumbering(activeClause.content)}
              </div>
            </div>

            {/* AI Analysis Card */}
            {activeClause.review && (activeClause.review.status === ReviewStatus.NON_COMPLIANT || 
             activeClause.review.status === ReviewStatus.UNCERTAIN) && (
              <>
                {/* Evidence & Reason */}
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 border-l-4 border-l-rose-500">
                  <h3 className="flex items-center gap-2 text-sm font-bold text-rose-600 uppercase tracking-wide mb-3">
                    <AlertTriangle size={16} />
                    风险分析
                  </h3>
                  <div className="space-y-4">
                    {activeClause.review.issues && activeClause.review.issues.length > 0 && (
                      <div>
                        <p className="text-sm font-semibold text-slate-700 mb-2">发现的问题：</p>
                        <ul className="list-disc list-inside space-y-1">
                          {activeClause.review.issues.map((issue, idx) => (
                            <li key={idx} className="text-slate-800 text-sm">{issue}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {activeClause.review.rationale && (
                      <div>
                        <p className="text-sm font-semibold text-slate-700 mb-1">判断理由：</p>
                        <p className="text-slate-800">{activeClause.review.rationale}</p>
                      </div>
                    )}
                    
                    {activeClause.review.evidence && activeClause.review.evidence.length > 0 && (
                      <div className="bg-rose-50 p-3 rounded-md">
                        <p className="text-xs font-bold text-rose-800 mb-2 flex items-center gap-1">
                          <BookOpen size={12} />
                          标准依据：
                        </p>
                        {activeClause.review.evidence.map((ev, idx) => (
                          <div key={idx} className="mb-2 last:mb-0">
                            <p className="text-sm font-medium text-rose-900">[{ev.rule_id}] {ev.rule_title}</p>
                            <p className="text-sm text-rose-800 italic">"{ev.matched_text}"</p>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {activeClause.review.status === ReviewStatus.UNCERTAIN && (
                      <>
                        {activeClause.review.uncertain_reasons && (
                          <div className="bg-amber-50 p-3 rounded-md">
                            <p className="text-xs font-bold text-amber-800 mb-1">不确定原因：</p>
                            <ul className="list-disc list-inside">
                              {activeClause.review.uncertain_reasons.map((reason, idx) => (
                                <li key={idx} className="text-sm text-amber-900">{reason}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {activeClause.review.missing_info && (
                          <div className="bg-amber-50 p-3 rounded-md">
                            <p className="text-xs font-bold text-amber-800 mb-1">需要补充的信息：</p>
                            <ul className="list-disc list-inside">
                              {activeClause.review.missing_info.map((info, idx) => (
                                <li key={idx} className="text-sm text-amber-900">{info}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Suggestions */}
                {(activeClause.review.suggested_cn || activeClause.review.suggested_en) && (
                  <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 border-l-4 border-l-emerald-500">
                    <h3 className="flex items-center gap-2 text-sm font-bold text-emerald-600 uppercase tracking-wide mb-4">
                      <Sparkles size={16} />
                      AI 修改建议
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      {activeClause.review.suggested_en && (
                        <div className="space-y-2">
                          <label className="text-xs font-semibold text-slate-500">建议修改 (英文)</label>
                          <div className="p-3 bg-emerald-50/50 rounded border border-emerald-100 text-sm text-slate-800 min-h-[80px]">
                            {cleanDuplicatedNumbering(activeClause.review.suggested_en)}
                          </div>
                        </div>
                      )}
                      {activeClause.review.suggested_cn && (
                        <div className="space-y-2">
                          <label className="text-xs font-semibold text-slate-500">建议修改 (中文)</label>
                          <div className="p-3 bg-emerald-50/50 rounded border border-emerald-100 text-sm text-slate-800 min-h-[80px]">
                            {cleanDuplicatedNumbering(activeClause.review.suggested_cn)}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex gap-2">
                      <button className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-100 text-emerald-700 rounded-md text-sm hover:bg-emerald-200 font-medium transition-colors">
                        <Check size={14} /> 采纳建议
                      </button>
                    </div>
                  </div>
                )}

                {/* Manual Override */}
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                  <h3 className="flex items-center gap-2 text-sm font-bold text-slate-600 uppercase tracking-wide mb-3">
                    <Edit3 size={16} />
                    人工修改
                  </h3>
                  <p className="text-xs text-slate-500 mb-2">
                    在下方输入您的修改意见，导出时将覆盖 AI 建议。
                  </p>
                  <textarea 
                    className="w-full border border-slate-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-slate-900 outline-none"
                    rows={4}
                    placeholder="在此输入修改后的条款文本..."
                    value={userEditText}
                    onChange={(e) => setUserEditText(e.target.value)}
                  />
                  <div className="flex justify-end mt-3">
                    <button 
                      onClick={handleSaveUserEdit}
                      disabled={savingEdit || !userEditText.trim()}
                      className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-md text-sm hover:bg-slate-800 disabled:opacity-50"
                    >
                      {savingEdit ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Save size={14} />
                      )}
                      保存修改
                    </button>
                  </div>
                </div>
              </>
            )}

            {activeClause.review?.status === ReviewStatus.COMPLIANT && (
              <div className="flex flex-col items-center justify-center p-12 text-center text-slate-400 bg-white rounded-xl shadow-sm border border-slate-200">
                <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center text-emerald-500 mb-4">
                  <Check size={32} />
                </div>
                <h3 className="text-lg font-semibold text-slate-700">条款合规</h3>
                <p className="text-sm">根据所提供的标准，未检测到风险。</p>
                {activeClause.review.rationale && (
                  <p className="text-sm text-slate-500 mt-2">{activeClause.review.rationale}</p>
                )}
              </div>
            )}

            {activeClause.review?.status === ReviewStatus.PENDING && (
              <div className="flex flex-col items-center justify-center p-12 text-center text-slate-400 bg-white rounded-xl shadow-sm border border-slate-200">
                <div className="w-16 h-16 border-4 border-slate-200 border-t-slate-400 rounded-full animate-spin mb-4"></div>
                <h3 className="text-lg font-semibold text-slate-700">正在分析...</h3>
                <p className="text-sm">AI 正在审核此条款</p>
              </div>
            )}
          </div>
        ) : (
          <div className="h-full flex flex-col items-center justify-center text-slate-400">
            <p>选择一个条款查看详情</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewPage;
